import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import Modal from 'react-modal';

import Image from './Image';

interface ImageGalleryProps {
  images: Array<{ url: string; description?: string | null } | string>;
  altText?: string;
}

const ImageGallery = ({
  images,
  altText = 'Hotel Image',
}: ImageGalleryProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const normalizedImages = useMemo(() => {
    return images.map((img) =>
      typeof img === 'string'
        ? { url: img, description: altText }
        : { url: img.url, description: img.description || altText },
    );
  }, [images, altText]);

  const openModal = (index: number = 0) => {
    setCurrentImageIndex(index);
    setModalIsOpen(true);
  };

  const closeModal = () => setModalIsOpen(false);

  const goToNextImage = () =>
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % normalizedImages.length,
    );

  const goToPrevImage = () =>
    setCurrentImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + normalizedImages.length) % normalizedImages.length,
    );

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1000,
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
    },
  };

  Modal.setAppElement('#root');

  const renderBentoGrid = () => {
    const imageCount = normalizedImages.length;

    if (imageCount === 0) {
      return (
        <div className="flex h-80 w-full items-center justify-center rounded-xl bg-gray-100 sm:h-96 lg:h-[500px]">
          <div className="text-center">
            <PhotoIcon className="mx-auto size-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No images available</p>
          </div>
        </div>
      );
    }

    // Always show maximum of 3 images
    const displayImages = normalizedImages.slice(0, 3);
    const remainingCount = Math.max(0, imageCount - 3);

    if (imageCount === 1) {
      return (
        <div className="relative w-full overflow-hidden rounded-xl">
          <div className="h-80 w-full bg-gray-100 sm:h-96 lg:h-[500px]">
            <button
              onClick={() => openModal(0)}
              className="group relative size-full cursor-pointer overflow-hidden rounded-xl"
            >
              <Image
                src={displayImages[0].url}
                alt={displayImages[0].description}
                className="size-full object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
            </button>
          </div>
        </div>
      );
    }

    if (imageCount === 2) {
      return (
        <div className="relative h-80 w-full overflow-hidden rounded-xl sm:h-96 lg:h-[500px]">
          <div className="grid size-full gap-2 sm:grid-cols-2">
            {displayImages.map((image, index) => (
              <button
                key={index}
                onClick={() => openModal(index)}
                className="group relative size-full cursor-pointer overflow-hidden rounded-lg"
              >
                <Image
                  src={image.url}
                  alt={image.description}
                  className="size-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
              </button>
            ))}
          </div>
        </div>
      );
    }

    // 3 or more images - show 3 images with overlay on third if more exist
    return (
      <div className="relative w-full overflow-hidden rounded-xl">
        <div className="grid gap-2 sm:h-96 sm:grid-cols-4 sm:grid-rows-2 sm:gap-2 lg:h-[500px]">
          {/* First image - takes up left half */}
          <button
            onClick={() => openModal(0)}
            className="group relative col-span-4 h-48 w-full cursor-pointer overflow-hidden rounded-lg sm:col-span-2 sm:row-span-2 sm:h-full"
          >
            <Image
              src={displayImages[0].url}
              alt={displayImages[0].description}
              className="size-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
          </button>

          {/* Second image - top right */}
          <button
            onClick={() => openModal(1)}
            className="group relative col-span-2 h-24 w-full cursor-pointer overflow-hidden rounded-lg sm:col-span-2 sm:row-span-1 sm:h-full"
          >
            <Image
              src={displayImages[1].url}
              alt={displayImages[1].description}
              className="size-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
          </button>

          {/* Third image - bottom right with optional overlay */}
          <button
            onClick={() => openModal(2)}
            className="group relative col-span-2 h-24 w-full cursor-pointer overflow-hidden rounded-lg sm:col-span-2 sm:row-span-1 sm:h-full"
          >
            <Image
              src={displayImages[2].url}
              alt={displayImages[2].description}
              className="size-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />

            {/* Show all photos overlay when there are more than 3 images */}
            {remainingCount > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white transition-colors duration-300 group-hover:bg-black/70">
                <div className="text-center">
                  <PhotoIcon className="mx-auto size-6 sm:size-8" />
                  <p className="mt-1 text-sm font-medium sm:text-base">
                    +{remainingCount} more
                  </p>
                </div>
              </div>
            )}
          </button>
        </div>

        {/* Show all photos button - positioned absolutely - only show if more than 3 images */}
        {remainingCount > 0 && (
          <button
            onClick={() => openModal(0)}
            className="absolute bottom-4 right-4 rounded-lg bg-white px-3 py-2 text-sm font-medium shadow-lg transition-all duration-200 hover:bg-gray-50 hover:shadow-xl sm:px-4 sm:py-2 sm:text-base"
            aria-label="View all photos"
          >
            <div className="flex items-center space-x-2">
              <PhotoIcon className="size-4 sm:size-5" />
              <span>Show all photos</span>
            </div>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderBentoGrid()}

      {modalIsOpen && (
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={customStyles}
          contentLabel="Image Gallery"
        >
          <div className="relative flex size-full flex-col items-center justify-center">
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-black shadow-lg transition-all duration-200 hover:bg-white hover:shadow-xl"
              aria-label="Close modal"
            >
              <XMarkIcon className="size-6" />
            </button>

            {/* Main image container */}
            <div className="relative flex size-full items-center justify-center p-8 sm:p-12">
              <Image
                src={normalizedImages[currentImageIndex]?.url}
                alt={normalizedImages[currentImageIndex]?.description}
                className="size-full rounded-lg object-contain shadow-2xl"
              />

              {/* Navigation buttons */}
              {normalizedImages.length > 1 && (
                <>
                  <button
                    onClick={goToPrevImage}
                    className="absolute left-4 rounded-full bg-white/90 p-3 text-black shadow-lg transition-all duration-200 hover:bg-white hover:shadow-xl"
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon className="size-6" />
                  </button>

                  <button
                    onClick={goToNextImage}
                    className="absolute right-4 rounded-full bg-white/90 p-3 text-black shadow-lg transition-all duration-200 hover:bg-white hover:shadow-xl"
                    aria-label="Next image"
                  >
                    <ChevronRightIcon className="size-6" />
                  </button>
                </>
              )}
            </div>

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-white">
              <span className="text-sm font-medium">
                {currentImageIndex + 1} / {normalizedImages.length}
              </span>
            </div>

            {/* Image description */}
            {normalizedImages[currentImageIndex]?.description && (
              <div className="absolute bottom-16 left-1/2 max-w-md -translate-x-1/2 rounded-lg bg-black/70 px-4 py-2 text-center text-white">
                <p className="text-sm">
                  {normalizedImages[currentImageIndex].description}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ImageGallery;
